import { useCategoriesProfilesWithBoards } from './useCategoriesProfilesWithBoards';
import { useContext, useEffect, useMemo, useState } from 'react';
import { SocialProfileWithBoardsAndMentionType } from '../modules/PostEditor/helpers/mapSelectedProfilesValues';
import { MappedCategoriesType } from '../modules/Publish/Categories/Categories.helpers';
import { DisconnectedProfilesType, PostEditorState, PostGroupsType } from '../models/post-editor';
import { profilesHelpers } from '../modules/Settings/Profiles/Profiles.helpers';
import { triggerErrorToast } from '../helpers/triggerErrorToast';
import {
  PostMultiMediaAttachMode,
  TimezoneSetting,
  PostQueuePosition,
  useGetBitlyAccountsQuery
} from '../models/graphql';
import { ImmutabilityContext } from '../modules/PostEditor/states/effects/customImmutabilityContext';
import { ApolloError } from '@apollo/client';
import { AppContext } from '../providers/AppProvider';
import { usePostEditorWorkspaceSettings } from './usePostEditorWorkspaceSettings';

export type WorkspaceSettingsForPostEditor = {
  evergreen: boolean;
  queuePosition: PostQueuePosition;
  scheduledTimezone: TimezoneSetting;
  varyMedia: PostMultiMediaAttachMode;
};

export type UsePostEditorResult = {
  categories: MappedCategoriesType[];
  error: ApolloError | undefined;
  initialState: PostEditorState;
  loading: boolean;
  profiles: SocialProfileWithBoardsAndMentionType[];
};

export const usePostEditorNew = (initial: PostEditorState, overrideWorkspaceSettings: boolean): UsePostEditorResult => {
  const { postEditorCategoriesProfiles: categoriesProfiles, error: categoriesProfilesError } =
    useCategoriesProfilesWithBoards();
  const [loading, setLoading] = useState<boolean>(true);
  const [initialState, setInitialState] = useState<PostEditorState>(initial);
  const { selectedWorkspaceId } = useContext(AppContext);
  const workspaceSettings = usePostEditorWorkspaceSettings();

  const { data: bitlyAccounts, error: bitlyAccountsError } = useGetBitlyAccountsQuery({
    variables: {
      workspaceId: selectedWorkspaceId
    }
  });

  const profiles: SocialProfileWithBoardsAndMentionType[] = useMemo(() => {
    const socialProfiles = categoriesProfiles?.workspace.socialProfiles || [];

    if (categoriesProfiles?.workspace.socialProfiles.length === 0) {
      setLoading(false);
    }
    return socialProfiles.map(profile => ({
      ...profile,
      mentionType: profilesHelpers.getMentionType(profile)
    }));
  }, [categoriesProfiles?.workspace.socialProfiles]);

  const categories: MappedCategoriesType[] = useMemo(
    () => categoriesProfiles?.workspace.categories || [],
    [categoriesProfiles?.workspace.categories]
  );

  useEffect(() => {
    if (profiles.length && categories.length && workspaceSettings && bitlyAccounts) {
      const { update: updateImmutable } = ImmutabilityContext;
      const { evergreen, queuePosition, scheduledTimezone, varyMedia } = workspaceSettings;
      const profilesWithBitly = bitlyAccounts.workspace.socialProfiles;
      const defaultBitly = bitlyAccounts.workspace.settings.defaultBitly;
      setInitialState(
        updateImmutable<PostEditorState>(initial, {
          postGroups: {
            $apply: (current: PostGroupsType[]) =>
              current.map(group =>
                updateImmutable<PostGroupsType>(group, {
                  bitlyAccount: {
                    profilesWithBitly: { $set: profilesWithBitly },
                    defaultBitly: { $set: defaultBitly }
                  },
                  groupPostAttributes: {
                    scheduling: {
                      evergreen: {
                        active: {
                          $apply: currentEvergrren => (overrideWorkspaceSettings ? currentEvergrren : evergreen)
                        }
                      },
                      queuePosition: {
                        $apply: currentQueuePosition =>
                          overrideWorkspaceSettings ? currentQueuePosition : queuePosition
                      },
                      scheduledTimezone: {
                        $apply: currentScheduledTimezone =>
                          overrideWorkspaceSettings ? currentScheduledTimezone : scheduledTimezone
                      }
                    },
                    content: {
                      varyMedia: {
                        $apply: currentVaryMedia => (overrideWorkspaceSettings ? currentVaryMedia : varyMedia)
                      }
                    }
                  }
                })
              )
          },
          disconnectedProfiles: {
            $apply: (current: DisconnectedProfilesType[]) => {
              if (!current.length) {
                return profiles.flatMap(p =>
                  !p.connected ? { id: p.id, shown: false, selected: false, __typename: p.__typename } : []
                );
              }
              const newProfiles = profiles.flatMap(p => {
                const currentIds = current.map(p => p.id);
                if (!p.connected && !currentIds.includes(p.id)) {
                  return {
                    id: p.id,
                    shown: false,
                    selected: false,
                    __typename: p.__typename
                  };
                }
                return [];
              });
              return [...current, ...newProfiles];
            }
          }
        })
      );
      setLoading(false);
      // const postGroup = cloneDeep((initialState || InitialPostEditorState).postGroups[0]);
    }
  }, [profiles, categories, workspaceSettings, initial, bitlyAccounts, overrideWorkspaceSettings]);

  useEffect(() => {
    if (categoriesProfilesError) {
      setLoading(false);
      triggerErrorToast('Error Fetching Profiles and Categories')(categoriesProfilesError);
    }
  }, [categoriesProfilesError]);

  useEffect(() => {
    if (bitlyAccountsError) {
      setLoading(false);
      triggerErrorToast('Error Fetching Bitly Accounts')(bitlyAccountsError);
    }
  }, [bitlyAccountsError]);

  return {
    loading,
    profiles,
    categories,
    initialState,
    error: categoriesProfilesError || bitlyAccountsError
  };
};
